/* eslint-disable no-nested-ternary */
import { InfoCircleOutlined } from "@ant-design/icons";
import { notification } from "antd";
import cn from "classnames";
import Humanize from "humanize-plus";
import { useState, useMemo, useCallback, useRef } from "react";
import { Draggable } from "react-beautiful-dnd";
import { VscGripper } from "react-icons/vsc";

import { useLoc } from "@app/shared/lib/hooks";
import { isEmpty } from "@app/shared/lib/is-empty";

import { DateTime } from "../../DateTime";
import { FileUploader, FileUploaderHandler } from "../../FileUploader";
import { SpaceSeparator } from "../../SpaceSeparator";
import { FileActions } from "../components/FileActions/FileActions";
import { FileName } from "../components/FileName/FileName";
import { FilePreview } from "../components/FilePreview/FilePreview";
import { FileDescriptionPopover } from "../FileDescriptionPopover/FileDescriptionPopover";
import { FileProps } from "../Files";

import styles from "./FileListItem.module.scss";

export enum FileUploadStatus {
  Uploading = "uploading",
  Uploaded = "uploaded",
  Error = "error",
}

export const FileListItem: React.FC<FileProps> = ({ file, ...props }) => {
  const loc = useLoc();
  const uploaderHandler = useRef<FileUploaderHandler>(null);
  const [progress, setProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<FileUploadStatus>(
    file?.nativeFile != null
      ? FileUploadStatus.Uploading
      : FileUploadStatus.Uploaded,
  );

  const retryUpload = useCallback(() => {
    setProgress(0);
    setUploadStatus(FileUploadStatus.Uploading);
  }, []);

  const handleFileReadyToUpload = useCallback(async () => {
    const res = await props.beforeUpload?.(file);
    if (res !== undefined && res === false) return; // cancel uploading
    await uploaderHandler.current?.upload();
  }, [file, props]);

  const fileSize = useMemo(
    () =>
      props.size === "lg" || props.size === "xl"
        ? "large"
        : props.size === "md"
          ? "middle"
          : "small",
    [props.size],
  );

  const fileContent = useMemo(
    () => (
      <div
        className={cn(
          styles.fl_list_item,
          "flex align-center flex-1 mb-5 mt-5",
        )}
        data-draggable={props.draggable}
        data-size={props.size}
      >
        <div className="flex flex-center flex-1 w-100">
          <FilePreview
            view="list"
            file={file}
            size={props.size}
            progress={progress}
            uploadStatus={uploadStatus}
            onPreview={() => props.onPreview(file)}
            onRetryUpload={() => retryUpload()}
            fileIconColor={props.fileIconColor}
            className={styles.fl_list_item__preview}
          />

          <div className="flex-1 flex align-center ellipsis">
            <div className="flex-1 flex flex-col justify-center ml-10 ellipsis">
              <FileName
                file={file}
                editable={props.editable}
                useExtension
                onRename={async (name) =>
                  (await props.onChange?.(file, { name })) ?? true
                }
                className={styles.fl_list_item__name}
              />
              <div className="fs-12">
                {uploadStatus === FileUploadStatus.Error ? (
                  <span className="fs-12 color-danger">
                    {loc.word("File.uploadFailedLabel", {
                      default: "Upload failed",
                    })}
                  </span>
                ) : (
                  <SpaceSeparator separator="dot" wrap={false}>
                    {props.dateCreatedVisible === true && (
                      <DateTime
                        date={file.createdDate}
                        format="LLL"
                        className="color-secondary"
                      />
                    )}
                    <span className="color-secondary">
                      {Humanize.fileSize(file.size)}
                    </span>
                    {props.descriptionVisible === true &&
                      uploadStatus === FileUploadStatus.Uploaded && (
                        <FileDescriptionPopover
                          file={file}
                          onSave={async (description) =>
                            (await props.onChange?.(file, { description })) ??
                            true
                          }
                        >
                          <InfoCircleOutlined
                            className={cn(
                              "ml-5",
                              !isEmpty(file.description)
                                ? "color-success"
                                : undefined,
                            )}
                          />
                        </FileDescriptionPopover>
                      )}
                  </SpaceSeparator>
                )}
              </div>
            </div>
            <div className="ml-10">
              <FileActions
                file={file}
                theme="dark"
                size={fileSize}
                deletable={props.deletable}
                downloadable={props.downloadable}
                replaceable={props.replaceable}
                uploadStatus={uploadStatus}
                onDownload={() => props.onDownload?.(file)}
                onRemove={() => props.onRemove?.(file)}
                onReplace={() => props.onReplace?.(file)}
                onUpload={() => retryUpload()}
                onAbort={() => uploaderHandler.current?.abort()}
              />
            </div>
          </div>
        </div>
      </div>
    ),
    [file, fileSize, loc, progress, props, retryUpload, uploadStatus],
  );

  return (
    <>
      {props.draggable ? (
        <Draggable draggableId={file.key} index={props.index}>
          {(provided, snap) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={provided.draggableProps.style}
              className={cn(
                "flex align-center",
                snap.isDragging ? "shadow-sm" : undefined,
              )}
            >
              {props.draggable === true && (
                <div
                  {...provided.dragHandleProps}
                  className={cn(styles.fl_drag_handle, "flex fs-18")}
                >
                  <VscGripper />
                </div>
              )}

              {fileContent}
            </div>
          )}
        </Draggable>
      ) : (
        fileContent
      )}

      {uploadStatus === FileUploadStatus.Uploading && (
        <FileUploader
          handler={(x) => {
            uploaderHandler.current = x;
          }}
          autostart={false}
          file={file}
          onReady={handleFileReadyToUpload}
          onProgress={(x) => setProgress(x.percent)}
          onComplete={async ({ file: completedFile }) => {
            completedFile.update(completedFile);
            setUploadStatus(FileUploadStatus.Uploaded);
            props.onUploaded?.(completedFile);
          }}
          uploadArgs={props.uploadArgs}
          onAbort={() => {
            if (file.nativeFile) {
              setUploadStatus(FileUploadStatus.Error);
              props.onAborted?.(file);
            }
          }}
          onError={() => {
            setUploadStatus(FileUploadStatus.Error);
            notification.error({
              message: loc.word("Error occured"),
              duration: 2,
            });
          }}
        />
      )}
    </>
  );
};
